import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, Inject, ChangeDetectorRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserMenuService, CategoryLatestReadingsVM, LogicalGroupReadingVM } from '@api/user-menu.service';
import { LineChartViewMode } from '@models/backend-generated-models/models';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';

export interface CategoryLatestReadingsUI extends CategoryLatestReadingsVM {
  selectedReading: LogicalGroupReadingVM | null;
  isLoading: boolean;
  errorMessage: string | null;
  chartData: any;
  stats: any | null;
  lastLeftDate?: any;
  lastRightDate?: any;
  trendSubscription?: any;
}

@Component({
  selector: 'app-modal-dashboard',
  templateUrl: './modal-dashboard.component.html',
  styleUrls: ['./modal-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalDashboardComponent implements OnInit, OnDestroy {

  referenceDate = new FormControl(moment());
  selectedPeriod: string = '1d';
  dashboardMode: 'my-utilities' | 'live-data' = 'my-utilities';

  latestReadings: CategoryLatestReadingsUI[] = [];
  latestLoading: boolean = false;
  latestError: string | null = null;

  private destroyed$ = new Subject<void>();
  private cancelPending$ = new Subject<void>();

  categories: any[] = [
    {
      key: 'Electricity',
      name: 'Electricity',
      matchedGroup: null,
      chartData: null,
      errorMessage: null,
      isLoading: false
    },
    {
      key: 'Gas',
      name: 'Gas',
      matchedGroup: null,
      chartData: null,
      errorMessage: null,
      isLoading: false
    },
    {
      key: 'Water',
      name: 'Water',
      matchedGroup: null,
      chartData: null,
      errorMessage: null,
      isLoading: false
    }
  ];

  buttonList: any[] = [
    { period: '1d', name: 'Live' },
    { period: '7d', name: 'W' },
    { period: '1m', name: 'M' },
    { period: '90d', name: '3M' },
    { period: '180d', name: '6M' },
    { period: '12m', name: '1Y' }
  ];

  constructor(
    private userMenuService: UserMenuService,
    private dialogRef: MatDialogRef<ModalDashboardComponent>,
    private cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public datas: { siteId: number }
  ) {}

  ngOnInit(): void {
    if (this.dashboardMode === 'live-data') {
      this.loadLatestReadings();
    } else {
      this.loadLogicalGroups();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.cancelPending$.next();
    this.cancelPending$.complete();

    this.categories.forEach(cat => {
      if (cat.chartSubscription) {
        cat.chartSubscription.unsubscribe();
      }
    });

    this.latestReadings.forEach(cat => {
      if (cat.trendSubscription) {
        cat.trendSubscription.unsubscribe();
      }
    });
  }

  switchMode(mode: 'my-utilities' | 'live-data') {
    this.dashboardMode = mode;
    if (mode === 'live-data') {
      this.loadLatestReadings();
    } else {
      this.loadLogicalGroups();
    }
  }

  loadLatestReadings() {
    this.cancelPending$.next();
    this.latestLoading = true;
    this.latestError = null;
    this.latestReadings = [];
    this.cdr.markForCheck();

    this.userMenuService.getLatestReadings$(this.datas.siteId)
      .pipe(
        takeUntil(this.destroyed$),
        takeUntil(this.cancelPending$)
      )
      .subscribe(readings => {
        this.latestLoading = false;
        this.latestReadings = readings.map(r => {
          const readingsList = r.readings || [];
          const selectedReading = readingsList.length > 0 ? readingsList[0] : null;
          const category: any = {
            ...r,
            selectedReading: selectedReading,
            isLoading: false,
            errorMessage: null,
            chartData: null,
            stats: null
          };

          if (selectedReading) {
            this.loadTrendForCategory(category);
          } else {
            category.errorMessage = "No readings found for the recent period.";
          }

          return category;
        });
        this.cdr.markForCheck();
      }, err => {
        this.latestLoading = false;
        this.latestError = 'Failed to load latest readings. Please try again.';
        console.error('Error loading latest readings:', err);
        this.cdr.markForCheck();
      });
  }

  onReadingChange(category: any, logicalGroupIdStr: string) {
    const lgId = parseInt(logicalGroupIdStr, 10);
    const matched = category.readings?.find((r: any) => r.logicalGroupId === lgId);
    if (matched) {
      category.selectedReading = matched;
      this.loadTrendForCategory(category);
    }
  }

  loadTrendForCategory(category: any) {
    if (!category.selectedReading || !category.selectedReading.logicalGroupId) {
      category.chartData = null;
      category.stats = null;
      category.errorMessage = "No readings found for the recent period.";
      category.isLoading = false;
      this.cdr.markForCheck();
      return;
    }

    const lgId = category.selectedReading.logicalGroupId;
    category.isLoading = true;
    category.errorMessage = null;
    category.chartData = null;
    category.stats = null;
    this.cdr.markForCheck();

    const refDate = moment(this.referenceDate.value);
    let dateRight = moment(refDate);
    let dateLeft = moment(refDate);

    const period = this.selectedPeriod;

    if (period === "1d") {
      dateLeft = moment(dateRight).subtract(1, 'days');
    }
    else if (period === "7d") {
      dateLeft = moment(dateRight).subtract(7, 'days');
    }
    else if (period === "1m") {
      dateLeft = moment(dateRight).subtract(1, 'months');
    }
    else if (period === "90d") {
      dateLeft = moment(dateRight).subtract(3, 'months');
    }
    else if (period === "180d") {
      dateLeft = moment(dateRight).subtract(6, 'months');
    }
    else if (period === "12m") {
      dateLeft = moment(dateRight).subtract(12, 'months');
    }

    category.lastLeftDate = dateLeft;
    category.lastRightDate = dateRight;
    category.dateRangeLabel = `Showing: ${dateLeft.format('DD-MMM-YYYY')} to ${dateRight.format('DD-MMM-YYYY')}`;

    if (category.trendSubscription) {
      category.trendSubscription.unsubscribe();
    }

    category.trendSubscription = this.userMenuService.getRptResourceConsumption$(
      this.datas.siteId,
      lgId,
      period,
      this.dateToString(dateLeft.toDate()),
      this.dateToString(dateRight.toDate())
    ).pipe(
      takeUntil(this.destroyed$),
      takeUntil(this.cancelPending$)
    ).subscribe(chart => {
      category.isLoading = false;
      let hasSeries = false;
      chart.data?.forEach(x => {
        if (x.series && x.series.length > 0) {
          hasSeries = true;
        }
      });

      if (!hasSeries) {
        const startStr = dateLeft.format('DD-MMM-YYYY');
        const endStr = dateRight.format('DD-MMM-YYYY');
        category.errorMessage = `No readings found for ${startStr} to ${endStr}. Try a wider period.`;
        category.chartData = null;
        category.stats = null;
      } else {
        category.chartData = chart.data;
        category.stats = this.calculateStats(chart.data, period, this.referenceDate.value);
      }
      this.cdr.markForCheck();
    }, err => {
      category.isLoading = false;
      category.errorMessage = "Failed to load dashboard data. Please try again.";
      category.chartData = null;
      category.stats = null;
      console.error('Error loading trend for logical group', lgId, err);
      this.cdr.markForCheck();
    });
  }

  loadLogicalGroups() {
    this.cancelPending$.next();
    this.categories.forEach(cat => {
      cat.isLoading = true;
      cat.errorMessage = null;
      cat.chartData = null;
      cat.stats = null;
    });
    this.cdr.markForCheck();

    this.userMenuService.getSiteLogicalGroups$(this.datas.siteId)
      .pipe(
        takeUntil(this.destroyed$),
        takeUntil(this.cancelPending$)
      )
      .subscribe(groups => {
        this.categories.forEach(cat => {
          cat.isLoading = false;
          let matched = null;
          if (cat.key === 'Electricity') {
            matched = groups.find(g => g.name?.toLowerCase().includes('total electricity')) ||
                      groups.find(g => g.name?.toLowerCase().includes('electricity'));
          } else if (cat.key === 'Gas') {
            matched = groups.find(g => g.name?.toLowerCase().includes('total gas')) ||
                      groups.find(g => g.name?.toLowerCase().includes('gas'));
          } else if (cat.key === 'Water') {
            matched = groups.find(g => g.name?.toLowerCase().includes('total water')) ||
                      groups.find(g => g.name?.toLowerCase().includes('water'));
          }

          if (matched) {
            cat.matchedGroup = matched;
            cat.name = this.cleanTitle(matched.name || cat.key);
            this.loadDataForCategory(cat, this.selectedPeriod);
          } else {
            cat.matchedGroup = null;
            cat.errorMessage = `No configured ${cat.key} group found for this site`;
          }
        });
        this.cdr.markForCheck();
      }, err => {
        this.categories.forEach(cat => {
          cat.isLoading = false;
          cat.matchedGroup = null;
          cat.errorMessage = "Failed to load site logical groups.";
          cat.stats = null;
        });
        console.error("Failed to load logical groups for site", err);
        this.cdr.markForCheck();
      });
  }

  onDateChange(event: any) {
    if (event && event.value) {
      this.referenceDate.setValue(event.value, { emitEvent: false });
    }
    this.reloadActiveTab();
  }

  onPeriodChange(period: string) {
    this.selectedPeriod = period;
    this.reloadActiveTab();
  }

  reloadActiveTab() {
    if (this.dashboardMode === 'my-utilities') {
      this.categories.forEach(cat => {
        if (cat.matchedGroup) {
          this.loadDataForCategory(cat, this.selectedPeriod);
        }
      });
    } else {
      this.latestReadings.forEach(cat => {
        if (cat.selectedReading) {
          this.loadTrendForCategory(cat);
        }
      });
    }
  }

  loadDataForCategory(category: any, period: string) {
    category.period = period;
    if (!category.matchedGroup) {
      category.chartData = null;
      category.stats = null;
      return;
    }

    const refDate = moment(this.referenceDate.value).startOf('day');
    let dateRight = moment(refDate);
    let dateLeft = moment(refDate);

    if (period === "1d") {
      dateLeft = moment(dateRight).subtract(1, 'days');
    }
    else if (period === "7d") {
      dateLeft = moment(dateRight).subtract(7, 'days');
    }
    else if (period === "1m") {
      dateLeft = moment(dateRight).subtract(1, 'months');
    }
    else if (period === "90d") {
      dateLeft = moment(dateRight).subtract(3, 'months');
    }
    else if (period === "180d") {
      dateLeft = moment(dateRight).subtract(6, 'months');
    }
    else if (period === "12m") {
      dateLeft = moment(dateRight).subtract(12, 'months');
    }

    category.lastLeftDate = dateLeft;
    category.lastRightDate = dateRight;
    category.dateRangeLabel = `Showing: ${dateLeft.format('DD-MMM-YYYY')} to ${dateRight.format('DD-MMM-YYYY')}`;

    category.isLoading = true;
    category.errorMessage = null;
    category.chartData = null;
    category.stats = null;
    this.cdr.markForCheck();

    if (category.chartSubscription) {
      category.chartSubscription.unsubscribe();
    }

    category.chartSubscription = this.userMenuService.getRptResourceConsumption$(
      this.datas.siteId,
      category.matchedGroup.logicalGroupId,
      period,
      this.dateToString(dateLeft.toDate()),
      this.dateToString(dateRight.toDate())
    ).pipe(
      takeUntil(this.destroyed$),
      takeUntil(this.cancelPending$)
    ).subscribe(chart => {
      category.isLoading = false;
      let hasSeries = false;
      chart.data?.forEach(x => {
        if (x.series && x.series.length > 0) {
          hasSeries = true;
        }
      });

      if (!hasSeries) {
        const startStr = dateLeft.format('DD-MMM-YYYY');
        const endStr = dateRight.format('DD-MMM-YYYY');
        category.errorMessage = `No readings found for ${startStr} to ${endStr}. Try a wider period such as M, 3M, 6M, or 1Y.`;
        category.chartData = null;
        category.stats = null;
      } else {
        category.chartData = chart.data;
        category.stats = this.calculateStats(chart.data, period, this.referenceDate.value);
      }
      this.cdr.markForCheck();
    }, err => {
      category.isLoading = false;
      category.errorMessage = "Failed to load dashboard data. Please try again.";
      category.chartData = null;
      category.stats = null;
      console.error("Dashboard Report API Error details:", err);
      this.cdr.markForCheck();
    });
  }

  onNoClick() {
    this.dialogRef.close();
  }

  dateToString(date: Date): string {
    if (date == undefined)
      return "YYYY-MM-DD HH:mm:ss";

    return moment(date).format('YYYY-MM-DDTHH:mm:ss');
  }

  cleanTitle(title: string): string {
    if (!title) return '';
    return title.replace(/\(for Dashboard purposes only\)/gi, '').trim();
  }

  calculateStats(data: LineChartViewMode[] | undefined, period: string, referenceDateValue: any) {
    let total = 0;
    let count = 0;
    let peak = -Infinity;
    let lastDateStr = 'N/A';

    data?.forEach(seriesItem => {
      seriesItem.series?.forEach(dp => {
        if (dp.value !== undefined && dp.value !== null) {
          total += dp.value;
          count++;
          if (dp.value > peak) {
            peak = dp.value;
          }
        }
      });

      if (seriesItem.series && seriesItem.series.length > 0) {
        const lastPoint = seriesItem.series[seriesItem.series.length - 1];
        if (lastPoint.name) {
          lastDateStr = this.parseLabelToDateStr(lastPoint.name, period, referenceDateValue);
        }
      }
    });

    if (count === 0) {
      return null;
    }

    const average = total / count;
    return {
      total: total.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 }),
      average: average.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      peak: peak === -Infinity ? '0.00' : peak.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      count: count,
      lastReading: lastDateStr
    };
  }

  exportToCSV(category: any) {
    if (!category.chartData || !category.chartData.length) {
      return;
    }

    const csvRows: string[] = [];
    csvRows.push('Series,Timestamp/Date,Value');

    category.chartData.forEach((seriesItem: any) => {
      const seriesName = seriesItem.name || category.name || category.key;
      seriesItem.series?.forEach((dp: any) => {
        const timestamp = dp.name || '';
        const value = dp.value !== undefined && dp.value !== null ? dp.value : '';
        const escapedSeriesName = `"${seriesName.replace(/"/g, '""')}"`;
        csvRows.push(`${escapedSeriesName},${timestamp},${value}`);
      });
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    const startStr = moment(category.lastLeftDate).format('YYYY-MM-DD');
    const endStr = moment(category.lastRightDate).format('YYYY-MM-DD');
    const resourceName = (category.key || 'resource').toLowerCase();
    const filename = `dashboard-${resourceName}-${startStr}-to-${endStr}.csv`;

    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  parseLabelToDateStr(label: string, period: string, referenceDateValue: any): string {
    if (!label) return 'N/A';

    if (period === '1d') {
      const match = label.match(/^(\d{2}):00h$/);
      if (match) {
        const hour = parseInt(match[1], 10);
        return moment(referenceDateValue).startOf('day').hour(hour).format('DD-MMM-YYYY HH:mm');
      }
    }

    if (period === '7d' || period === '1m' || period === '90d' || period === '180d') {
      const cleanedLabel = label.replace(/^[a-zA-Z]+\s+/, '');
      const dateMoment = moment(cleanedLabel, 'DD.MM.YY');
      if (dateMoment.isValid()) {
        return dateMoment.format('DD-MMM-YYYY');
      }
    }

    if (period === '12m') {
      const dateMoment = moment(label, 'MMM YY');
      if (dateMoment.isValid()) {
        return dateMoment.format('MMM-YYYY');
      }
    }

    return label;
  }
}
